import type { Candidate, CommitResult, Corrections, Frame, ItemInfo, Problem, Session, Vec3 } from '../fsm/machine';

// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────
// Il client HTTP di Dim: un solo punto che conosce /api/v1, i Problem Details (RFC 9457) e il token di sessione.
// Il token sta in sessionStorage (per scheda, muore con la scheda; il server lo scade comunque dopo 8 ore).
// Ogni errore ha una classe: l'App decide la transizione in base al tipo, mai leggendo un messaggio.
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────

export class DimApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly detail: string,
    public readonly correlationId: string | null,
  ) {
    super(detail);
    this.name = 'DimApiError';
  }
}

/** Nessuna risposta: rete assente, timeout, server giù. Si distingue da un 4xx perché la mossa giusta è "riprova", non "cambia". */
export class DimNetworkError extends Error {
  constructor(cause: unknown) {
    super('Rete assente o server non raggiungibile');
    this.name = 'DimNetworkError';
    this.cause = cause;
  }
}

const TOKEN_KEY = 'dim.session.token';

export const sessionToken = {
  get: (): string | null => sessionStorage.getItem(TOKEN_KEY),
  set: (token: string): void => sessionStorage.setItem(TOKEN_KEY, token),
  clear: (): void => sessionStorage.removeItem(TOKEN_KEY),
};

export function problemFrom(status: number, body: unknown): Problem {
  const b = (body ?? {}) as Record<string, unknown>;
  return {
    status,
    code: typeof b.code === 'string' ? b.code : typeof b.title === 'string' ? b.title : `http-${status}`,
    detail: typeof b.detail === 'string' ? b.detail : `Errore ${status}`,
  };
}

interface RequestOptions {
  json?: unknown;
  form?: FormData;
  auth?: boolean;
  timeoutMs?: number;
}

async function request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opts.json !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.auth !== false) {
    const token = sessionToken.get();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers,
      body: opts.form ?? (opts.json !== undefined ? JSON.stringify(opts.json) : null),
      signal: AbortSignal.timeout(opts.timeoutMs ?? 20_000),
      credentials: 'omit',
      referrerPolicy: 'no-referrer',
    });
  } catch (cause) {
    throw new DimNetworkError(cause);
  }

  if (response.status === 204) return undefined as T;
  const text = await response.text();
  const body: unknown = text.length > 0 ? safeJson(text) : null;
  if (!response.ok) {
    const p = problemFrom(response.status, body);
    const corr = body && typeof body === 'object' && 'correlationId' in body ? String((body as Record<string, unknown>).correlationId) : null;
    throw new DimApiError(p.status, p.code, p.detail, corr);
  }
  return body as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return { detail: text };
  }
}

export interface RedeemResponse {
  sessionToken: string;
  expiresAtUtc: string;
  iss: string;
  codSoc: string;
  login: string;
  roles: string[];
  returnUrl: string | null;
  /** Il pallet già scansionato in Quick, validato dal server; null se Quick non ne ha passato uno. */
  itemId: string | null;
}

export interface ImagePoint {
  x: number;
  y: number;
}

export interface ImageLine {
  a: ImagePoint;
  b: ImagePoint;
}

/** Risposta di /api/v1/auto-measure: i numeri solo se `shown`; le linee in pixel della foto inviata, per disegnarle sopra. */
export interface AutoMeasureScene {
  tiltDeg: number;
  sideDeg: number;
  cameraHeightMm: number;
  distanceMm: number;
  baseHeightMm: number | null;
  topSeenFromAbove: boolean;
  calibration: string;
  /** «sensore» se la verticale viene dalla gravità, «linee» se dalla foto. */
  verticalSource: string;
  sensorPhotoDisagreementDeg: number | null;
}

export interface AutoMeasureResult {
  shown: boolean;
  refuseReasons: string[];
  /** I problemi che non impediscono il numero: si mostrano sotto. */
  warnings: string[];
  /** «misura» (geometria) o «stima» (proporzione semplice); null se la foto è da rifare. */
  method: string | null;
  frontMm: number;
  depthMm: number;
  heightMm: number | null;
  volumeM3: number | null;
  imageWidthPx: number;
  imageHeightPx: number;
  top: ImageLine | null;
  floor: ImageLine | null;
  deck: ImageLine | null;
  /** Come è stata fatta la foto, ricostruito dalla geometria: c'è anche quando la foto è rifiutata. */
  scene: AutoMeasureScene | null;
  /** Falso con una fotocamera sconosciuta: la lente è ipotizzata e il numero è una stima. */
  calibratedLens: boolean;
  stageMilliseconds: Record<string, number>;
  algorithmVersion: string;
  saved: boolean;
}

export interface ClientConfig {
  gates: { minBlurVariance: number; maxClipLowShare: number; maxClipHighShare: number; maxGlareShare: number };
  profiles: string[];
  defaultAccuracyClass: string;
  automaticEnabled: boolean;
  captureSessionMinutes: number;
}

export interface CaptureRequestBody {
  schemaVersion: '1.0';
  profile: 'pallet';
  itemId: string;
  accuracyClass: string;
  device: { fingerprint: string; model: string; captureWidthPx: number; captureHeightPx: number };
  calibrationProfileVersion: string | null;
  gates: { blurVar: number; clipLow: number; clipHigh: number; glare: number };
  gravity: { x: number; y: number; z: number } | null;
}

export const api = {
  async redeem(iss: string, code: string, returnUrl: string | null, state: string | null): Promise<Session> {
    const r = await request<RedeemResponse>('POST', '/api/v1/auth/redeem', { json: { iss, code, returnUrl }, auth: false });
    sessionToken.set(r.sessionToken);
    return { token: r.sessionToken, iss: r.iss, codSoc: r.codSoc, login: r.login, roles: r.roles, expiresAtUtc: r.expiresAtUtc, returnUrl: r.returnUrl, state, itemId: r.itemId ?? null };
  },

  config(): Promise<ClientConfig> {
    return request<ClientConfig>('GET', '/api/v1/config');
  },

  /** Null = pallet sconosciuto (404); un errore di rete NON blocca: l'App lo traduce in "non verificato". */
  async lookupPallet(idPallet: string): Promise<ItemInfo | null> {
    try {
      return await request<ItemInfo>('GET', `/api/v1/items/pallet/${encodeURIComponent(idPallet)}`, { timeoutMs: 5_000 });
    } catch (e) {
      if (e instanceof DimApiError && e.status === 404) return null;
      throw e;
    }
  },

  /** multipart: `request` (JSON) + `frame0` (gli stessi byte del Blob, nessuna ricodifica). */
  computeCandidate(body: CaptureRequestBody, frame: Frame): Promise<Candidate> {
    const form = new FormData();
    form.append('request', JSON.stringify(body));
    form.append('frame0', frame.blob, 'frame0.jpg');
    return request<Candidate>('POST', '/api/v1/capture-sessions', { form, timeoutMs: 30_000 });
  },

  manualCandidate(body: { itemId: string; frontMm: number; depthMm: number; heightFloorToTopMm: number; reason: string; deviceFingerprint: string }): Promise<Candidate> {
    return request<Candidate>('POST', '/api/v1/capture-sessions/manual', { json: body });
  },

  commit(sessionId: string, corrections: Corrections | null, capturedAtUtc: string | null): Promise<CommitResult> {
    return request<CommitResult>('POST', '/api/v1/measurements', { json: { sessionId, confirmed: true, corrections, capturedAtUtc } });
  },

  /** Dim separata da Quick: utente e password di EMS nella società scelta. Stessa sessione del riscatto (token in sessionStorage). */
  async login(iss: string, codSoc: string, login: string, password: string): Promise<RedeemResponse> {
    const r = await request<RedeemResponse>('POST', '/api/v1/auth/login', { json: { iss, codSoc, login, password }, auth: false });
    sessionToken.set(r.sessionToken);
    return r;
  },

  /**
   * La foto così com'è (gli stessi byte, nessuna ricodifica), il tipo scelto e la gravità letta allo scatto (sistema della fotocamera, m/s²): con la
   * gravità la verticale viene dal sensore, senza dalle linee della foto. Il server misura in ~8 s e ne fa una alla volta: attesa lunga.
   */
  autoMeasure(photo: Blob, frontTypeMm: number, gravity: Vec3 | null = null): Promise<AutoMeasureResult> {
    const form = new FormData();
    form.append('photo', photo, 'foto.jpg');
    form.append('frontTypeMm', String(frontTypeMm));
    if (gravity) {
      form.append('gravityX', String(gravity.x));
      form.append('gravityY', String(gravity.y));
      form.append('gravityZ', String(gravity.z));
    }
    return request<AutoMeasureResult>('POST', '/api/v1/auto-measure', { form, timeoutMs: 120_000 });
  },

  async logout(): Promise<void> {
    try {
      await request<void>('POST', '/api/v1/auth/logout');
    } finally {
      sessionToken.clear();
    }
  },
};
